import Axios, { AxiosInstance, AxiosPromise } from 'axios';
import { fetchEventSource, FetchEventSourceInit, EventSourceMessage } from '@microsoft/fetch-event-source';
import { redirectUnauthorised } from './RedirectUnauthorised';
import { addCsrfHeader } from './Csrf';

export interface UrlParams {
    [key: string]: any;
}

export interface Headers {
    [key: string]: string;
}

type FetchEventSourceProps = FetchEventSourceInit & {
    urlParams?: UrlParams;
};

class SseTimeoutError extends Error {}
export class SseUnauthorisedError extends Error {}
class SseInternalError extends Error {}
export class SseInterruptedError extends Error {}

/**
 * Abstract API controller to call API commands from the backend
 */
export abstract class AbstractApi {
    /**
     * API handler page name. Needs to be defined in the concrete class.
     */
    protected static page: string;

    /** @return {AxiosInstance} the Axios instance to use in API calls */
    protected static axios = (): AxiosInstance => Axios;

    /**
     * Construct API command URL
     * @param  {string} module module name
     * @param  {string} cmd command name
     * @param  {UrlParams} urlParams request params
     * @return {string} URL
     */
    private static url(module: string, cmd: string, urlParams: UrlParams = {}): string {
        const params = new URLSearchParams();
        params.append('page', this.page);
        params.append('module', module);
        params.append('cmd', cmd);
        Object.keys(urlParams).forEach((key: string) => {
            params.append(key, urlParams[key]);
        });
        return '?' + params.toString();
    }

    /**
     * Make GET request to API and return results in a promise
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {UrlParams} params additional parameters
     * @param {Headers|undefined} headers
     * @return {AxiosPromise}
     */
    public static get<T = any>(
        module: string,
        cmd: string,
        params: UrlParams = {},
        headers: Headers | undefined = undefined
    ): AxiosPromise<T> {
        return this.axios().get(this.url(module, cmd), {
            params,
            headers,
        });
    }

    /**
     * Perform a GET request to API that returns a binary file and return the results in a promise
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {string} mimeType the expected MIME type of the file
     * @param {UrlParams} params additional parameters
     * @return {AxiosPromise}
     */
    public static getFile(
        module: string,
        cmd: string,
        mimeType: string = 'application/octet-stream',
        params: UrlParams = {}
    ): AxiosPromise<any> {
        return this.axios().get(this.url(module, cmd), {
            params,
            headers: { Accept: mimeType },
            responseType: 'arraybuffer',
        });
    }

    /**
     * Make POST request to API with JSON data and return results in a promise
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {any} json request json object
     * @param {UrlParams} requestParams request parameters
     * @return {AxiosPromise}
     */
    public static postJson<T = any>(module: string, cmd: string, json: any = {}, requestParams: UrlParams = {}): AxiosPromise<T> {
        return this.axios().post(this.url(module, cmd), json, requestParams);
    }

    /**
     * Send a POST request to an API endpoint with a JSON body and a header containing a CSRF token.
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {any} json request json object
     * @param {UrlParams} requestParams request parameters
     * @return {AxiosPromise} the response
     * @throws {CsrfTokenError} if no CSRF token is found
     */
    public static postJsonWithCsrf<T = any>(module: string, cmd: string, json: any = {}, requestParams: UrlParams = {}): AxiosPromise<T> {
        return this.axios().post(this.url(module, cmd), json, {
            ...requestParams,
            headers: {
                ...requestParams.headers,
                ...addCsrfHeader(),
            },
        });
    }

    /**
     * Make POST request to API with params in URL and return results in a promise
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {UrlParams} urlParams request parameters
     * @param {Headers|undefined} headers
     * @return {AxiosPromise}
     */
    public static postUrl<T = any>(
        module: string,
        cmd: string,
        urlParams: UrlParams = {},
        headers: Headers | undefined = undefined
    ): AxiosPromise<T> {
        return this.axios().post(this.url(module, cmd, urlParams), undefined, { headers });
    }

    /**
     * Send a POST request to an API endpoint with URL parameters and a header containing a CSRF token.
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {UrlParams} urlParams request parameters
     * @param {Headers} headers
     * @return {AxiosPromise}
     */
    public static postUrlWithCsrf<T = any>(module: string, cmd: string, urlParams: UrlParams = {}, headers: Headers = {}): AxiosPromise<T> {
        return this.axios().post(this.url(module, cmd, urlParams), undefined, {
            headers: {
                ...headers,
                ...addCsrfHeader(),
            },
        });
    }

    /**
     * Make POST request to API that the browser keeps in flight after this
     * document is gone, so the caller can navigate away without waiting for it.
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {UrlParams} urlParams request parameters
     * @return {boolean} false when the browser refused to queue the request
     */
    public static beacon(module: string, cmd: string, urlParams: UrlParams = {}): boolean {
        return navigator.sendBeacon(this.url(module, cmd, urlParams));
    }

    /**
     * Make POST request to API with the body as form data and return results in a promise
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {FormData} formData form input
     * @return {AxiosPromise}
     */
    public static postForm<T = any>(module: string, cmd: string, formData: FormData): AxiosPromise<T> {
        return this.axios()(this.url(module, cmd), {
            method: 'post',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            data: formData,
        });
    }

    /**
     * Make POST request to API with the body as form data and a header containing a CSRF token.
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {FormData} formData form input
     * @return {AxiosPromise}
     * @throws {CsrfTokenError} if no CSRF token is found
     */
    public static postFormWithCsrf<T = any>(module: string, cmd: string, formData: FormData): AxiosPromise<T> {
        return Axios(this.url(module, cmd), {
            method: 'post',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...addCsrfHeader(),
            },
            data: formData,
        });
    }

    /**
     * Processes a stream of server-sent events.
     *
     * @param {string} module module name
     * @param {string} cmd command to execute
     * @param {FetchEventSourceProps} params event handlers and optional query parameters
     * @param {number} idleTimeoutMs timeout between heartbeats, not reconnection when <= 0
     * @param {function} disableWatchdogOnMessage function for removing autoreconnection on message receive
     *
     * @returns {AbortController} interface represents a controller
     * object that allows you to abort one or more Web requests as and when desired.
     */
    public static eventSource(
        module: string,
        cmd: string,
        params: FetchEventSourceProps,
        idleTimeoutMs: number = 25_000,
        disableWatchdogOnMessage?: (message: EventSourceMessage) => boolean
    ): AbortController {
        const controller = new AbortController();
        const signal = params.signal ?? controller.signal;

        let watchdog: ReturnType<typeof setTimeout> | null = null;

        const armWatchdog = () => {
            if (idleTimeoutMs <= 0) return;
            if (watchdog) clearTimeout(watchdog);
            watchdog = setTimeout(() => {
                console.info('[SSE] NoHeartbeat _/\\_/\\____/\\__________ ');
                params.onclose?.();
                controller.abort();
            }, idleTimeoutMs);
        };

        const disarmWatchdog = () => {
            if (watchdog) {
                console.info('[SSE] DisarmeWatchdog');
                clearTimeout(watchdog);
            }
        };

        const onBeforeUnload = () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            disarmWatchdog();
            params.onerror?.(new SseInterruptedError());
        };

        signal.addEventListener('abort', () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        });

        fetchEventSource(this.url(module, cmd, params?.urlParams), {
            signal: signal,
            openWhenHidden: true,
            onopen: async (response: Response) => {
                if (response.ok) {
                    console.info('[SSE] Opened');
                    armWatchdog();
                    window.addEventListener('beforeunload', onBeforeUnload);
                    await params.onopen?.(response);
                    return;
                }
                if (response.status === 401) throw new SseUnauthorisedError(response.statusText);
                if (response.status === 500) throw new SseInternalError(response.statusText);
                return Promise.reject(response);
            },
            onmessage: (message: EventSourceMessage) => {
                if (disableWatchdogOnMessage?.(message)) {
                    disarmWatchdog();
                } else {
                    armWatchdog();
                }
                // Skip empty keep-alive messages.
                if (!message?.data) return;
                params.onmessage?.(message);
            },
            onclose: () => {
                disarmWatchdog();
                window.removeEventListener('beforeunload', onBeforeUnload);

                // Backend closed (e.g., time limit). Signal retry by throwing timeout.
                console.info('[SSE] CloseTimeoutError');
                params.onclose?.();
                throw new SseTimeoutError('[SSE] timeout/close; retrying');
            },
            onerror: (err: Error) => {
                disarmWatchdog();
                window.removeEventListener('beforeunload', onBeforeUnload);

                if (err instanceof SseTimeoutError) {
                    // Returning nothing causes a retry.
                    console.warn('[SSE] TimeoutError');
                    return;
                }
                if (err instanceof SseUnauthorisedError) {
                    console.warn('[SSE] UnauthorisedError');
                    redirectUnauthorised('expired_session');
                    // propagate to caller so they can END their channel if desired
                    return params.onerror?.(err);
                }
                console.error(`[SSE] Error: ${err?.message || 'Unknown error'}`);
                return params.onerror?.(err);
            },
        });

        return controller;
    }
}
